/**
 * Login page.
 *
 * Branded split layout on desktop, full-bleed on mobile. Uses the Phase 1
 * Button + Input + Card primitives. Error states surface ApiError messages.
 * Includes a small "demo credentials" affordance in dev so the seeded users
 * are easy to discover.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api-client";
import { isDeviceConnected } from "@/offline/connectivity";
import { useOffline } from "@/offline/OfflineProvider";
import { forgetOfflineOwner, isOfflineOwner, readOfflineOwner, type OfflineOwner } from "@/offline/owner";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { loadSavedCredentials, saveCredentials, clearSavedCredentials } from "@/lib/saved-credentials";
import { roleHomePath } from "@/routes/role-home";
import { env } from "@/lib/env";

interface LocationState {
  from?: { pathname: string };
}

/* Scoped CSS for the animated maritime auth scene (teal). All selectors
 * are prefixed with ic-auth- so they can't collide with the design system. */
const AUTH_SCENE_CSS = `
.ic-authscene { background:#063b3a; }
.ic-auth-gradient {
  background: linear-gradient(135deg,
    hsl(187 70% 22%) 0%,
    hsl(187 60% 30%) 30%,
    hsl(190 55% 38%) 55%,
    hsl(196 60% 46%) 100%);
  background-size: 200% 200%;
  animation: icAuthGradient 18s ease infinite;
}
@keyframes icAuthGradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
.ic-auth-glow {
  position:absolute; border-radius:9999px; filter: blur(60px); pointer-events:none;
}
.ic-auth-glow--1 {
  width:420px; height:420px; top:-120px; right:-100px;
  background: radial-gradient(circle, rgba(94,234,212,0.35), transparent 70%);
  animation: icAuthFloat1 16s ease-in-out infinite;
}
.ic-auth-glow--2 {
  width:360px; height:360px; bottom:-120px; left:-80px;
  background: radial-gradient(circle, rgba(56,189,248,0.28), transparent 70%);
  animation: icAuthFloat2 20s ease-in-out infinite;
}
@keyframes icAuthFloat1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-40px,40px)} }
@keyframes icAuthFloat2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(40px,-30px)} }

.ic-auth-radar {
  position:absolute; top:50%; left:50%; width:760px; height:760px;
  transform:translate(-50%,-50%); opacity:0.5; pointer-events:none;
}
.ic-auth-sweep { animation: icAuthSpin 6s linear infinite; }
.ic-auth-needle { animation: icAuthSpin 24s linear infinite; }
.ic-auth-rings circle { animation: icAuthPulse 6s ease-in-out infinite; }
.ic-auth-rings circle:nth-child(2){ animation-delay:.6s }
.ic-auth-rings circle:nth-child(3){ animation-delay:1.2s }
.ic-auth-rings circle:nth-child(4){ animation-delay:1.8s }
@keyframes icAuthSpin { to { transform: rotate(360deg); } }
@keyframes icAuthPulse { 0%,100%{opacity:.5} 50%{opacity:1} }

.ic-auth-ping {
  position:absolute; top:50%; left:50%; width:40px; height:40px; border-radius:9999px;
  border:1px solid rgba(255,255,255,0.4); transform:translate(-50%,-50%) scale(0);
  pointer-events:none;
}
.ic-auth-ping--1 { animation: icAuthPing 5s ease-out infinite; }
.ic-auth-ping--2 { animation: icAuthPing 5s ease-out infinite 1.6s; }
.ic-auth-ping--3 { animation: icAuthPing 5s ease-out infinite 3.2s; }
@keyframes icAuthPing {
  0% { transform:translate(-50%,-50%) scale(0); opacity:.7; }
  100% { transform:translate(-50%,-50%) scale(11); opacity:0; }
}

.ic-auth-particles { position:absolute; inset:0; overflow:hidden; pointer-events:none; }
.ic-auth-particle {
  position:absolute; bottom:-10px; width:6px; height:6px; border-radius:9999px;
  background: rgba(204,251,241,0.9); box-shadow:0 0 8px rgba(94,234,212,0.8);
  animation: icAuthRise linear infinite;
}
@keyframes icAuthRise {
  0% { transform:translateY(0) translateX(0); opacity:0; }
  10% { opacity:1; }
  90% { opacity:1; }
  100% { transform:translateY(-105vh) translateX(24px); opacity:0; }
}

.ic-auth-wave { position:absolute; bottom:0; left:0; width:200%; height:200px; pointer-events:none; }
.ic-auth-wave-path { animation: icAuthWave 12s ease-in-out infinite; }
.ic-auth-wave-path--2 { animation-duration:9s; animation-direction:reverse; }
@keyframes icAuthWave { 0%,100%{transform:translateX(0)} 50%{transform:translateX(-80px)} }

.ic-auth-logo { animation: icAuthLogo 4s ease-in-out infinite; }
@keyframes icAuthLogo { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }

.ic-auth-reveal { opacity:0; transform:translateY(14px); animation: icAuthReveal .8s cubic-bezier(.2,.7,.2,1) forwards; }
@keyframes icAuthReveal { to { opacity:1; transform:translateY(0); } }

@media (prefers-reduced-motion: reduce) {
  .ic-auth-gradient, .ic-auth-glow--1, .ic-auth-glow--2, .ic-auth-sweep, .ic-auth-needle,
  .ic-auth-rings circle, .ic-auth-ping, .ic-auth-particle, .ic-auth-wave-path, .ic-auth-logo {
    animation: none !important;
  }
  .ic-auth-reveal { opacity:1; transform:none; animation:none; }
}
`;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as LocationState | null)?.from?.pathname;

  // The login screen always renders in the default brand color (#3194A0)
  // regardless of any company brand that was active in this tab before.
  // BrandingContext also handles this on auth-state change, but we belt-
  // and-braces here in case the route renders before that effect fires.
  useEffect(() => {
    const root = document.documentElement;
    const props = [
      "--primary", "--primary-hover", "--primary-active",
      "--primary-fg", "--primary-soft",
      "--ai-from", "--ai-to", "--brand-primary",
    ];
    for (const p of props) root.style.removeProperty(p);
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [remember, setRemember] = useState(false);
  const { repo } = useOffline();
  /** Someone else's unsynced offline changes are on this device. */
  const [unsynced, setUnsynced] = useState<{ owner: OfflineOwner; pending: number } | null>(null);
  const [discarding, setDiscarding] = useState(false);

  // Prefill from previously-saved credentials ("Remember me"). Works on web and
  // the Capacitor apps (Preferences). Tick the box so the user can see it's on.
  useEffect(() => {
    void loadSavedCredentials().then((c) => {
      if (!c) return;
      setEmail(c.loginId);
      setPassword(c.password);
      setRemember(true);
    });
  }, []);


  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // A shared device may hold another person's changes that haven't synced.
    // They can only be sent under that person's sign-in, and signing in as
    // someone else clears the device — so ask first instead of erasing them.
    const owner = readOfflineOwner();
    if (owner && !isOfflineOwner(owner, email)) {
      const pending = await repo.pendingCount().catch(() => 0);
      if (pending > 0) {
        setUnsynced({ owner, pending });
        return;
      }
    }
    await signIn();
  }

  /** The user chose to sign in anyway: discard the other person's changes. */
  async function discardAndSignIn() {
    setDiscarding(true);
    try {
      await repo.clear();
      forgetOfflineOwner();
      setUnsynced(null);
    } finally {
      setDiscarding(false);
    }
    await signIn();
  }

  async function signIn() {
    setSubmitting(true);
    try {
      const result = await login(
        email.trim().toLowerCase(),
        password,
        mfaStep ? mfaToken.trim() : undefined,
      );
      if (typeof result === "object" && result !== null && "mfaRequired" in result) {
        // Account has MFA — switch to the code step.
        setMfaStep(true);
        setSubmitting(false);
        return;
      }
      // Persist (or clear) credentials per the "Remember me" choice.
      if (remember) await saveCredentials(email.trim().toLowerCase(), password);
      else await clearSavedCredentials();
      const name = result.firstName ? `, ${result.firstName}` : "";
      toast.success(result.isFirstLogin ? "Welcome aboard" : "Signed in", {
        position: "top-center",
        description: result.isFirstLogin
          ? `Welcome aboard${name}! Your login was successful.`
          : `Signed in successfully. Welcome back${name}.`,
      });
      const target = fromPath && fromPath !== "/login" ? fromPath : roleHomePath(result.role);
      navigate(target, { replace: true });
    } catch (err) {
      const opts = { position: "top-center" as const };
      // Distinguish "device offline" from "server unreachable" using the
      // reliable native connectivity check (navigator.onLine lies in a WebView).
      const connected = await isDeviceConnected();

      if (!(err instanceof ApiError)) {
        toast.error(
          connected ? "Sign-in failed" : "You appear to be offline",
          {
            ...opts,
            description: connected
              ? "Couldn't reach the server. Please try again in a moment."
              : "An internet connection is required to sign in. Reconnect and try again.",
          },
        );
      } else if (err.status === 0) {
        // The request never got an HTTP response. If the device IS online, this
        // is a server-reachability problem (wrong address / server down / TLS),
        // NOT an offline device — say so instead of blaming the connection.
        toast.error(connected ? "Can't reach the server" : "You appear to be offline", {
          ...opts,
          description: connected
            ? "Your device is online, but the iCheck server couldn't be reached. Check the server address and that it's running, then try again."
            : "An internet connection is required to sign in. Reconnect and try again.",
        });
      } else if (err.status === 401) {
        toast.error(mfaStep ? "Invalid authentication code" : "Sign-in failed", {
          ...opts,
          description: mfaStep
            ? "That authentication code is invalid or expired."
            : "The email or password you entered is incorrect.",
        });
      } else {
        const titles: Record<string, string> = {
          ACCOUNT_NOT_ACTIVATED:      "Account not activated",
          ACCOUNT_DISABLED:           "Account disabled",
          COMPANY_DISABLED:           "Company account disabled",
          VESSEL_INACTIVE:            "Vessel inactive",
          AWAITING_VESSEL_ASSIGNMENT: "Not assigned to a vessel",
          RATE_LIMITED:               "Too many attempts",
        };
        toast.error(titles[err.code] ?? "Sign-in failed", {
          ...opts,
          description: err.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function fillDemo(role: "super_admin" | "admin" | "shore_staff" | "vessel") {
    const map: Record<string, string> = {
      "super_admin": "super@demo-shipping.example",
      "admin":       "admin@demo-shipping.example",
      "shore_staff": "shore@demo-shipping.example",
      "vessel":      "master@demo-shipping.example",
    };
    setEmail(map[role] ?? "");
    setPassword("Change-Me-Now-1!");
  }

  return (
    <div className="grid min-h-svh grid-cols-1 bg-bg lg:grid-cols-2">
      <style>{AUTH_SCENE_CSS}</style>
      {/* ── Left: animated brand panel ─────────────────────────── */}
      <div className="relative hidden overflow-hidden lg:block ic-authscene">
        {/* animated gradient base */}
        <div className="ic-auth-gradient absolute inset-0" />
        {/* drifting depth glows */}
        <div className="ic-auth-glow ic-auth-glow--1" />
        <div className="ic-auth-glow ic-auth-glow--2" />

        {/* radar / compass sweep + sonar rings */}
        <svg className="ic-auth-radar" viewBox="0 0 600 600" aria-hidden="true">
          <defs>
            <radialGradient id="sweepGrad" cx="5%" cy="45%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.0)" />
              <stop offset="70%" stopColor="rgba(255,255,255,0.0)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.18)" />
            </radialGradient>
          </defs>
          {/* concentric compass rings */}
          <g className="ic-auth-rings" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1">
            <circle cx="300" cy="300" r="90" />
            <circle cx="300" cy="300" r="160" />
            <circle cx="300" cy="300" r="230" />
            <circle cx="300" cy="300" r="300" />
          </g>
          {/* crosshairs */}
          <g stroke="rgba(255,255,255,0.10)" strokeWidth="1">
            <line x1="300" y1="0" x2="300" y2="600" />
            <line x1="0" y1="300" x2="600" y2="300" />
          </g>
          {/* rotating sweep wedge */}
          <g className="ic-auth-sweep" style={{ transformOrigin: "300px 300px" }}>
            <path d="M300 300 L300 0 A300 300 0 0 1 560 150 Z" fill="url(#sweepGrad)" />
          </g>
          {/* compass needle */}
          <g className="ic-auth-needle" style={{ transformOrigin: "300px 300px" }}>
            <polygon points="300,150 312,300 300,310 288,300" fill="rgba(255,255,255,0.55)" />
            <polygon points="300,450 312,300 300,290 288,300" fill="rgba(255,255,255,0.22)" />
            <circle cx="300" cy="300" r="6" fill="rgba(255,255,255,0.7)" />
          </g>
        </svg>

        {/* expanding sonar pings */}
        <span className="ic-auth-ping ic-auth-ping--1" />
        <span className="ic-auth-ping ic-auth-ping--2" />
        <span className="ic-auth-ping ic-auth-ping--3" />

        {/* rising particles */}
        <div className="ic-auth-particles" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="ic-auth-particle" style={{
              left: `${(i * 7 + 4) % 100}%`,
              animationDelay: `${(i % 7) * 1.3}s`,
              animationDuration: `${9 + (i % 5) * 2.5}s`,
              opacity: 0.15 + (i % 4) * 0.12,
            }} />
          ))}
        </div>

        {/* animated wave at the bottom */}
        <svg className="ic-auth-wave" viewBox="0 0 1200 200" preserveAspectRatio="none" aria-hidden="true">
          <path className="ic-auth-wave-path" d="M0,100 C150,160 350,40 600,100 C850,160 1050,40 1200,100 L1200,200 L0,200 Z" fill="rgba(255,255,255,0.06)" />
          <path className="ic-auth-wave-path ic-auth-wave-path--2" d="M0,120 C200,60 400,180 600,120 C800,60 1000,180 1200,120 L1200,200 L0,200 Z" fill="rgba(255,255,255,0.05)" />
        </svg>

        {/* content */}
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3 ic-auth-reveal" style={{ animationDelay: "0.05s" }}>
            <div className="grid h-32 w-32 place-items-center rounded-2xl bg-white/15 backdrop-blur ic-auth-logo overflow-hidden">
              <img src="/login-logo.png" alt="" className="h-24 w-24 object-contain" />
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="max-w-md text-3xl font-semibold leading-tight tracking-tight ic-auth-reveal" style={{ animationDelay: "0.18s" }}>
              The maritime SIRE 2.0 inspection platform, redesigned.
            </h1>
            <p className="max-w-md text-base leading-relaxed text-white/80 ic-auth-reveal" style={{ animationDelay: "0.32s" }}>
              Run inspections offline, surface risk faster with built-in AI insights,
              and keep your fleet audit-ready — from any device.
            </p>
          </div>

          <a
            href="https://elementree.co.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-white/70 hover:text-white transition-colors underline-offset-4 hover:underline ic-auth-reveal w-fit"
            style={{ animationDelay: "0.46s" }}
          >
            © {new Date().getFullYear()} Element Tree
          </a>
        </div>
      </div>

      {/* ── Right: form ───────────────────────────────────────── */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* App mark + wordmark. We render the wordmark as TEXT (not the wide
              login-logo.png, whose dark navy text was unreadable at small sizes
              and invisible in dark mode) so it reads in both themes. */}
          <div className="mb-6 flex flex-col items-center gap-3">
            <img src="/app-logo.png" alt="" className="h-24 w-auto" />
            <div className="text-center">
              <div className="text-3xl font-bold tracking-tight text-fg">iCheck</div>
              <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.15em] text-fg-muted">
                SIRE 2.0 · Maritime Inspections
              </div>
            </div>
          </div>

          <h2 className="text-center text-2xl font-semibold tracking-tight text-fg">Welcome back</h2>
          <p className="mt-1 text-center text-sm text-fg-muted">Sign in to continue to your fleet.</p>

          <Card className="mt-8" variant="raised">
            <CardContent className="p-6">
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Mail or Username</Label>
                  <Input
                    id="email"
                    /* Not type="email" — crew log in with rank IDs like
                       "choff.9739989" which browsers reject as not-an-email. */
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    placeholder="Enter your email or username here"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    placeholder="Enter your password here"
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPwd((s) => !s)}
                        className="text-fg-muted hover:text-fg"
                        aria-label={showPwd ? "Hide password" : "Show password"}
                      >
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    }
                  />
                </div>

                {mfaStep ? (
                  <div className="space-y-2">
                    <Label htmlFor="mfa">Authentication code</Label>
                    <Input
                      id="mfa"
                      type="text"
                      inputMode="numeric"
                      value={mfaToken}
                      onChange={(e) => setMfaToken(e.target.value.replace(/[^0-9a-zA-Z-]/g, "").slice(0, 10))}
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      placeholder="6-digit code or backup code"
                      className="font-mono tracking-widest"
                    />
                    <p className="text-xs text-fg-muted">
                      Enter the 6-digit code from your authenticator app, or a backup code.
                    </p>
                  </div>
                ) : null}

                {/* Save credentials on this device (web + Android + iOS). */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={remember}
                    onCheckedChange={(v) => setRemember(v === true)}
                  />
                  <Label htmlFor="remember" className="cursor-pointer text-sm font-normal text-fg-muted">
                    Remember me on this device
                  </Label>
                </div>

                <Button type="submit" className="w-full" loading={submitting} disabled={submitting}>
                  {mfaStep ? "Verify code" : "Sign in"} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </form>

                {/* Demo-account quick-fill chips: DEV only. import.meta.env.DEV
                    is statically `false` in any `vite build`, so this whole
                    block (incl. the demo credentials in fillDemo) is dead-code
                    eliminated from every production/staging/capacitor bundle. */}
                {import.meta.env.DEV ? (
                  <div className="mt-6 border-t border-border pt-4">
                    <p className="mb-2 text-xs font-medium text-fg-muted">Accounts</p>
                    <div className="flex flex-wrap gap-2">
                      {(["super_admin", "admin", "shore_staff", "vessel"] as const).map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => fillDemo(r)}
                          className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs text-fg-muted transition hover:border-primary/40 hover:text-fg"
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
            </CardContent>
          </Card>

          {/* Page footer — mirrors the other auth screens so support is one
              click away from any sign-in problem, with the legal link sitting
              on its own line just beneath it. */}
          <div className="mt-5 text-center text-xs text-fg-muted">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span>Trouble signing in?</span>
              <span aria-hidden>•</span>
              <Link to="/support" className="hover:text-fg underline-offset-4 hover:underline">
                Need help?
              </Link>
            </div>
            <div className="mt-1.5">
              <Link to="/privacy" className="hover:text-fg underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={unsynced !== null}
        onOpenChange={(o) => { if (!o) setUnsynced(null); }}
        tone="danger"
        title="Unsynced changes on this device"
        description={unsynced ? (
          <>
            <strong>{unsynced.owner.name}</strong> has {unsynced.pending} change{unsynced.pending === 1 ? "" : "s"} on this
            device that {unsynced.pending === 1 ? "hasn't" : "haven't"} reached iCheck yet. To keep {unsynced.pending === 1 ? "it" : "them"},
            {" "}{unsynced.owner.name} should sign in here while online so {unsynced.pending === 1 ? "it syncs" : "they sync"}.
            If you sign in as someone else, {unsynced.pending === 1 ? "it is" : "they are"} deleted.
          </>
        ) : undefined}
        cancelLabel="Cancel"
        confirmLabel="Delete their changes and sign in"
        pending={discarding || submitting}
        onConfirm={() => void discardAndSignIn()}
      />
    </div>
  );
}
